/**
 * Lightweight nav strip for the /admin/* surface. The primary navigation
 * lives in the page layout (top bar) — this is the secondary tab strip
 * shown above each admin sub-page so the user can jump between them.
 */
import { Link, useRouterState } from '@tanstack/react-router'

interface NavItem {
  to: string
  label: string
}

const ITEMS: readonly NavItem[] = [
  { to: '/admin', label: 'Overview' },
  { to: '/admin/orgs', label: 'Organizations' },
  { to: '/admin/failed-jobs', label: 'Failed jobs' },
  { to: '/admin/client-errors', label: 'Client errors' },
  { to: '/admin/feature-flags', label: 'Feature flags' },
  { to: '/admin/audit-log', label: 'Audit log' },
  { to: '/admin/archival', label: 'Archival' },
  { to: '/admin/health', label: 'Health' },
] as const

export function AdminNav() {
  const router = useRouterState()
  const path = router.location.pathname.replace(/\/$/, '') || '/admin'

  return (
    <nav
      aria-label="Admin navigation"
      className="flex flex-nowrap overflow-x-auto gap-1 border-b border-[var(--border)] mb-4"
    >
      {ITEMS.map((item) => {
        // Match the deepest segment that's a prefix of `path`. /admin → only
        // matches exactly so we don't highlight Overview on every sub-page.
        const isActive = item.to === '/admin' ? path === '/admin' : path.startsWith(item.to)
        return (
          <Link
            key={item.to}
            to={item.to}
            aria-current={isActive ? 'page' : undefined}
            className={[
              'shrink-0 px-3 py-2 text-[13px] no-underline rounded-t border-b-2 -mb-px transition-colors',
              isActive
                ? 'font-semibold text-[var(--text)] border-[var(--accent)] bg-[var(--bg-2)]'
                : 'text-[var(--text-2)] border-transparent hover:text-[var(--text)] hover:bg-[var(--bg-2)]',
            ].join(' ')}
          >
            {item.label}
          </Link>
        )
      })}
    </nav>
  )
}
